/**
 * RWBR — THE MONEY A SINGLE RACE MOVES.
 *
 * Sagliano's PART TWO, executed in his own order, for one race weekend:
 *
 *     1. TV MONEY      "calculated at the conclusion of each Race Week
 *                       (qualifying)" (SAG/20) — so it happens BEFORE the race,
 *                       off the starting grid and last week's race, which is
 *                       why a wreck sells this week's television rather than
 *                       last week's.
 *     2. FAN ATTENDANCE roll the crowd, price the tickets, take 20% for the
 *                       track purse (SAG/22).
 *     3. WINNINGS      purse + TV money (SAG/23), split down the finishing
 *                       order, then 40% driver / 60% team.
 *     4. CONTINGENCY   position and accomplishment awards. Drivers keep it all.
 *     5. GUARANTEES    sponsorship, automaker support and salary, each prorated
 *                       across the 36 races.
 *     6. COSTS         the 36 Mb weekly race cost, R&D, testing, repairs, fines.
 *     7. SPONSORSHIP   post-race signings, and the bad-mouthing risk.
 *
 * WHAT IT RETURNS. A stream of `MoneyEvent`s and nothing else that matters. The
 * breakdowns (`tv`, `attendance`, `purse`) are there so the interface can print
 * the arithmetic — "1666 → 1832 → 1760, league takes 176, track keeps 1030,
 * purse gets 554" is a broadcast graphic, not a debug log — but they are
 * derived views, and no balance anywhere is read from them.
 *
 * PURITY AND ORDER. Pure function of its input. It builds its own dice from the
 * seed and rolls them in a FIXED, DOCUMENTED order (see `rollOrder` below),
 * because a finance layer whose numbers move when an unrelated loop is
 * reordered is worse than no finance layer.
 */

import { Dice } from '../dice.ts';
import type { DiceRecord, Modifier } from '../ruleEvent.ts';
import type { DriverCard, Trophy } from '../types.ts';
import type { TrackCard } from '../turn.ts';
import type { ChartId, RaceWeekResult } from '../raceWeek.ts';
import type { RaceResult } from '../race.ts';
import type { SeasonStandings } from '../standings.ts';
import {
  driverAccount,
  LEAGUE_ACCOUNT,
  moneyEvent,
  teamAccount,
  trackAccount,
  transfer,
  type MoneyEvent,
} from './money.ts';
import {
  ATTENDANCE,
  ATTENDANCE_ADJUSTMENTS,
  AUTOMAKER_SUPPORT_YEARLY,
  badMouthOutcome,
  CAR_REPLACEMENT_MB,
  CITE_ATTENDANCE,
  CITE_AUTOMAKER,
  CITE_BAD_MOUTHING,
  CITE_CAR_REPLACEMENT,
  CITE_CONTINGENCY,
  CITE_DRIVER_FINES,
  CITE_PURSE,
  CITE_PURSE_SPLIT,
  CITE_RACE_COST,
  CITE_RD,
  CITE_RD_SCALE,
  CITE_REPAIR_COST,
  CITE_SALARY,
  CITE_SPONSOR_SIGNUP,
  CITE_SPONSORSHIP,
  CITE_TEAM_FINES,
  CITE_TESTING,
  CITE_TV_INTERNAL,
  CITE_TV_MONEY,
  CITE_TV_SPLIT,
  CONTINGENCY_AWARD_MB,
  CONTINGENCY_FINISH_PLACES,
  CONTINGENCY_LAST_TURN_WIN_MB,
  CONTINGENCY_POINTS_LEADER_MB,
  CONTINGENCY_SPONSORS,
  DEFAULT_TRACK_CAPACITY,
  DRIVER_PURSE_SHARE,
  DRIVER_SALARY_YEARLY,
  GLOOMY_ECONOMY_ROLL,
  LEAGUE_R_AND_D_SCALE,
  perRace,
  purseBandFor,
  purseShareFor,
  qualityTier,
  RACES_PER_MONTH,
  RACES_PER_TEST,
  RD_COST_MB,
  REPAIR_COST_MB,
  SAGLIANO_BASELINE_RACES,
  SPONSOR_CAPS,
  SPONSOR_SIGNUP_SUCCEEDS_AT,
  SPONSORSHIP_YEARLY,
  TEAM_PURSE_SHARE,
  TEST_COST_MB,
  TICKET_PRICE_MB,
  TRACK_PURSE_SHARE,
  TV_BASE_REVENUE_MB,
  TV_EXTERNAL,
  TV_INTERNAL_PCT,
  TV_LEAGUE_SHARE,
  TV_PURSE_SHARE,
  WEEKLY_RACE_COST_MB,
  type QualityTier,
  type SponsorTier,
} from './tables.ts';
import { sponsorNameFor } from './sponsorNames.ts';
import {
  AUTO_RENEW_PRIMARY,
  CITE_AUTO_RENEW,
  CITE_ONE_RACE_ASSOCIATE,
  isAltercation,
  isBadMouth,
  isRaceWeekDamage,
  ONE_RACE_ASSOCIATE,
  tallyWeek,
  type WeekSponsorTally,
} from './sponsorCodes.ts';

// ---------------------------------------------------------------------------
// Who the finance layer thinks people are
// ---------------------------------------------------------------------------

export interface FinanceDriverProfile {
  driverId: string;
  name: string;
  /** Independents get their own single-driver team id. Every driver has one. */
  teamId: string;
  /** Number of qualities on the card, 1-5 after clamping. Prices everything. */
  qualities: QualityTier;
  trophy: Trophy;
  /**
   * SAG/21's internal TV factor reads "Each female or one- or two-quality
   * driver gets the pole (new faces)".
   *
   * RULING (D-029 §4): our cards carry no gender, so the clause is carried as
   * an optional flag defaulting to false and the rule fires on the quality
   * clause alone. The flag exists rather than being deleted because the mailed
   * card set may name drivers whose gender is known, and a rule silently
   * dropped is a rule that never comes back.
   */
  isFemale?: boolean;
}

export function profileFromCard(card: DriverCard, teamId?: string): FinanceDriverProfile {
  return {
    driverId: card.driverId,
    name: card.name,
    teamId: teamId ?? card.teamId ?? `indep-${card.driverId}`,
    qualities: qualityTier(card.qualities.length),
    trophy: card.trophy,
  };
}

/** SAG/21 — gold and silver are the "same old faces"; bronze and none are new. */
export function isSameOldFace(trophy: Trophy): boolean {
  return trophy === 'gold' || trophy === 'silver';
}

// ---------------------------------------------------------------------------
// Contracts — the one thing here that is genuinely STATE
// ---------------------------------------------------------------------------

/**
 * A sponsorship a driver holds. This is state in the honest sense: a contract
 * is a thing you possess, not a number derived from history. Money is never
 * stored here — the contract says what it pays per year, and the ledger records
 * every race it actually paid.
 */
export interface SponsorContract {
  tier: SponsorTier;
  /**
   * The quality count the deal was priced at. Frozen at signing: SAG/14 prices
   * sponsorship off qualities "before the season begins", and re-rating happens
   * between seasons, so a mid-season figure cannot move.
   */
  pricedAtQualities: QualityTier;
  yearlyMb: number;
  /** 0 = held from the season's start. */
  signedRace: number;
  /** RW52 — pays one race's associate money and expires. */
  oneRaceOnly?: boolean;
  /**
   * D-EZ-96 — the name painted on the car. Invented, in the house tone of
   * SAG/24's contingency sponsors; never a real brand. Hashed from the
   * contract's identity, so naming it moves no dice.
   */
  sponsorName?: string;
  /** D-EZ-96 — seasons this sponsor has been on the car, this one included. */
  seasonsHeld?: number;
  /** D-EZ-96 — signed over the winter for next season; not yet raced under. */
  signedInWinter?: boolean;
  /**
   * D-EZ-91 — quality tiers below the driver's own that this deal is priced at.
   * SAG/19's replacement primary is signed "one quality lower"; carried across
   * winters, it is re-priced off the driver's new card at the same offset.
   */
  priceOffset?: number;
}

export interface DriverFinanceState {
  driverId: string;
  primary: SponsorContract | null;
  major: SponsorContract[];
  associate: SponsorContract[];
  /** Races remaining on a "lose sponsorship for one month" penalty (SAG/17). */
  primaryBlackoutRaces: number;
  /** SAG/17's "-1 Warned previously this racing season". */
  badMouthWarnings: number;
  /** SAG/19 — Z54 came up; the primary renews next season regardless of points. */
  autoRenewPrimary: boolean;
}

export function newDriverFinanceState(profile: FinanceDriverProfile, nameKey?: string): DriverFinanceState {
  return {
    driverId: profile.driverId,
    primary: {
      tier: 'primary',
      pricedAtQualities: profile.qualities,
      yearlyMb: SPONSORSHIP_YEARLY.primary[profile.qualities],
      signedRace: 0,
      sponsorName: sponsorNameFor(`${nameKey ?? 'league'}|${profile.driverId}|primary|0`),
      seasonsHeld: 1,
    },
    major: [],
    associate: [],
    primaryBlackoutRaces: 0,
    badMouthWarnings: 0,
    autoRenewPrimary: false,
  };
}

export interface FinanceState {
  byDriver: Record<string, DriverFinanceState>;
}

// ---------------------------------------------------------------------------
// Input and output
// ---------------------------------------------------------------------------

/** What last week's race did that this week's television cares about (SAG/21). */
export interface PreviousRaceFacts {
  bigOne: boolean;
  superRare: boolean;
  /** The winner took the lead on the final turn via SURGE or a DUEL. */
  lastTurnWin: boolean;
}

export const NO_PREVIOUS_RACE: PreviousRaceFacts = { bigOne: false, superRare: false, lastTurnWin: false };

export interface RaceFinanceOptions {
  /** Season length. Defaults to Sagliano's 36; everything prorates off it. */
  totalRaces?: number;
  /**
   * SAG/30 — "For every five races in a cup season, the Team engages in
   * testing… but not two races consecutively."
   *
   * RULING (D-029 §5): every fifth race (5, 10, 15…), which gives 7 tests over
   * 36 and can never land on consecutive races. Overridable per league.
   */
  testRaces?: readonly number[];
  /**
   * Z26 and Z56 print "both drivers fined" and no amount, and Sagliano's table
   * does not key either row. RULING (D-029 §6): 10 Mb, matching Y13's "serious
   * altercation" — the nearest printed offence, and the same severity band.
   */
  unpricedFineMb?: number;
  /**
   * THE ONE PLACE THIS ENGINE WILL DEVIATE FROM THE PAGE ON PURPOSE.
   *
   * Run as printed, Sagliano's economy does not balance, and it is not close.
   * The measurement is in `OPEN_RULING_002_economy.md`; the short version is
   * that his four core tables — sponsorship, automaker support, weekly race
   * cost and salary — were tuned against each other to leave every quality tier
   * a designed margin of +164/+274/+344/+334/+164 Mb a year, and then R&D (935
   * Mb/yr expected) and testing (350 Mb/yr) were added on top of that balance
   * without anything being adjusted to pay for them. Race winnings supply about
   * 300 Mb a year to an average team. The shortfall is roughly 1,000 Mb per
   * team per year, near-uniform across every tier, and 37 of 53 teams finish a
   * season insolvent.
   *
   * RULED (D-030): R&D is quartered and everything else stands exactly as
   * printed. That takes insolvency from 112 of 159 teams to 62 across three
   * measured seasons — a one-quality team lands at roughly break-even and a
   * five-quality team at +3,292, so teams can still fail and running well still
   * pays. Pass `ECONOMY_AS_PRINTED` to get the page back, deficits included.
   */
  economy?: EconomyTuning;
}

export interface EconomyTuning {
  /**
   * Multiplier on the SAG/30 R&D chart. 1 = as printed (~26 Mb a race, which
   * is 72% of the entire 36 Mb weekly operating budget, from one roll).
   */
  rdScale?: number;
  /** Multiplier on the SAG/30 50 Mb test cost. 1 = as printed. */
  testScale?: number;
  /**
   * The share of the track's television money that goes into the purse rather
   * than the track owner's pocket. Default 0.35 (SAG/21, D-011). Of the 60,000
   * Mb of annual TV revenue the guide describes, 41,000 currently goes to
   * parties that have no expenses anywhere in this game.
   */
  tvPurseShare?: number;
}

/** The guide, verbatim. Insolvent by design — see OPEN_RULING_002_economy.md. */
export const ECONOMY_AS_PRINTED: EconomyTuning = { rdScale: 1, testScale: 1, tvPurseShare: TV_PURSE_SHARE };

/** What the league actually runs on (D-030). This is the default. */
export const ECONOMY_LEAGUE: EconomyTuning = {
  rdScale: LEAGUE_R_AND_D_SCALE,
  testScale: 1,
  tvPurseShare: TV_PURSE_SHARE,
};

export interface AttendanceBreakdown {
  capacity: number;
  /** True when the track card prints no capacity and DEFAULT_TRACK_CAPACITY stood in. */
  capacityAssumed: boolean;
  roll: number;
  factor: string;
  basePct: number;
  adjustments: Modifier[];
  finalPct: number;
  attendance: number;
  ticketPriceMb: number;
  admissionsMb: number;
  trackPurseMb: number;
}

export interface TvMoneyBreakdown {
  baseMb: number;
  externalRoll: number;
  externalFactor: string;
  externalPct: number;
  modifiedBaseMb: number;
  internal: Modifier[];
  internalPct: number;
  finalBaseMb: number;
  leagueShareMb: number;
  trackGrossMb: number;
  trackKeepsMb: number;
  purseContributionMb: number;
}

export interface PursePayout {
  place: number;
  driverId: string;
  teamId: string;
  band: string;
  sharePct: number;
  totalMb: number;
  driverMb: number;
  teamMb: number;
}

export interface PurseBreakdown {
  trackPurseMb: number;
  tvMoneyMb: number;
  winningsMb: number;
  payouts: PursePayout[];
  contingencyMb: number;
  /** SAG/24 — "Add Contingency Sponsor Awards to Total Winnings. The result is the race's Prize." */
  prizeMb: number;
  /** What the accountant pocketed (SAG/24) — winnings the field did not claim. */
  unclaimedMb: number;
}

export type SponsorChangeKind =
  | 'signed-major'
  | 'signed-associate'
  | 'signed-one-race-associate'
  | 'declined'
  | 'primary-blackout'
  | 'primary-warned'
  | 'primary-ignored'
  | 'auto-renew';

export interface SponsorChange {
  driverId: string;
  kind: SponsorChangeKind;
  /** D-EZ-96 — the sponsor's name, where the change is a signing. */
  sponsorName?: string;
  description: string;
  ruleCitation: string;
  dice: DiceRecord[];
  modifiers: Modifier[];
}

export interface RaceFinanceInput {
  seasonId: string;
  raceNumber: number;
  seed: number;
  track: TrackCard;
  week: RaceWeekResult;
  race: RaceResult;
  /**
   * Standings AFTER this race — SAG/24 keys the points-leader award to them,
   * and SAG/15 keys sponsor eligibility to "Driver rating (DR)".
   *
   * Optional, because a standalone exhibition race has no championship. Without
   * it the points-leader contingency is not paid and no driver carries a DR,
   * which is the correct answer rather than a degraded one: there is no leader
   * of a standings table that does not exist.
   */
  standings?: SeasonStandings;
  profiles: readonly FinanceDriverProfile[];
  state: FinanceState;
  previous?: PreviousRaceFacts;
  options?: RaceFinanceOptions;
}

export interface RaceFinanceResult {
  raceNumber: number;
  attendance: AttendanceBreakdown;
  tv: TvMoneyBreakdown;
  purse: PurseBreakdown;
  events: MoneyEvent[];
  /** Each driver's Race Week sponsor scoring, for the season-end fold. */
  weekTallies: Record<string, WeekSponsorTally>;
  /** Who was on television this week — the media-presence counter. */
  tvAppearanceIds: string[];
  sponsorChanges: SponsorChange[];
  /** SAG/18 factor 6 — a 2 on either the TV or attendance chart. */
  gloomyEconomy: boolean;
  state: FinanceState;
}

// ---------------------------------------------------------------------------
// The engine
// ---------------------------------------------------------------------------

/**
 * THE ROLL ORDER, fixed and documented:
 *
 *   1. TV MONEY external 2d6
 *   2. FAN ATTENDANCE 2d6
 *   3. R&D 2d6, once per team, teams in ascending id order
 *   4. REPAIRS 2d6, once per damaged car, in finishing order
 *   5. SPONSOR SIGNUP 2d6, eligible drivers in finishing order
 *   6. BAD MOUTHING 2d6, affected drivers in finishing order
 *
 * Nothing may roll outside this sequence. Adding a roll means appending to the
 * end of it or changing this comment, deliberately.
 */
export function runRaceFinance(input: RaceFinanceInput): RaceFinanceResult {
  const { seasonId, raceNumber, track, week, race, profiles } = input;
  const standings = input.standings;
  const opts = input.options ?? {};
  const totalRaces = opts.totalRaces ?? SAGLIANO_BASELINE_RACES;
  const previous = input.previous ?? NO_PREVIOUS_RACE;
  const unpricedFineMb = opts.unpricedFineMb ?? 10;
  const testRaces = opts.testRaces ?? defaultTestRaces(totalRaces);
  const economy: Required<EconomyTuning> = {
    rdScale: opts.economy?.rdScale ?? LEAGUE_R_AND_D_SCALE,
    testScale: opts.economy?.testScale ?? 1,
    tvPurseShare: opts.economy?.tvPurseShare ?? TV_PURSE_SHARE,
  };

  const dice = new Dice(input.seed);
  const events: MoneyEvent[] = [];
  const sponsorChanges: SponsorChange[] = [];
  const state = cloneState(input.state);

  const byId = new Map(profiles.map((p) => [p.driverId, p]));
  const profileOf = (id: string): FinanceDriverProfile | undefined => byId.get(id);
  const push = (...e: MoneyEvent[]): void => void events.push(...e);

  // ---- 1. TV MONEY (SAG/20-21) --------------------------------------------
  const tv = computeTvMoney({ dice, week, race, previous, profiles: byId, purseShare: economy.tvPurseShare });

  // ---- 2. FAN ATTENDANCE → TRACK PURSE (SAG/22) ---------------------------
  const attendance = computeAttendance({ dice, track, raceNumber, totalRaces, feuds: week.feuds.length > 0 });

  const gloomyEconomy = tv.externalRoll === GLOOMY_ECONOMY_ROLL || attendance.roll === GLOOMY_ECONOMY_ROLL;

  // The abstracted books, so every Megabuck has a source (SAG/12).
  push(
    moneyEvent({
      seasonId,
      raceNumber,
      phase: 'race-week',
      account: trackAccount(track.id),
      amountMb: attendance.admissionsMb,
      category: 'admissions',
      description: `${attendance.attendance.toLocaleString('en-US')} through the gate at ${attendance.finalPct * 100}% of capacity — ${attendance.factor}.`,
      ruleCitation: CITE_ATTENDANCE,
      dice: [{ kind: '2d6-sum', dice: [], total: attendance.roll, label: 'FAN ATTENDANCE' }],
      modifiers: attendance.adjustments,
    }),
    moneyEvent({
      seasonId,
      raceNumber,
      phase: 'race-week',
      account: LEAGUE_ACCOUNT,
      amountMb: tv.leagueShareMb,
      category: 'tv-money',
      description: `The league's 10% of ${round2(tv.finalBaseMb)} Mb in television revenue.`,
      ruleCitation: `${CITE_TV_MONEY}; ${CITE_TV_INTERNAL}; ${CITE_TV_SPLIT}`,
      modifiers: tv.internal,
    }),
    moneyEvent({
      seasonId,
      raceNumber,
      phase: 'race-week',
      account: trackAccount(track.id),
      amountMb: tv.trackKeepsMb,
      category: 'tv-money',
      description: `The track keeps 65% of the ${round2(tv.trackGrossMb)} Mb left after the league's cut.`,
      ruleCitation: CITE_TV_SPLIT,
    }),
  );

  // ---- 3. WINNINGS (SAG/23) -----------------------------------------------
  const winningsMb = attendance.trackPurseMb + tv.purseContributionMb;
  const payouts: PursePayout[] = [];
  let claimedMb = 0;

  for (const entry of race.finishingOrder) {
    const sharePct = purseShareFor(entry.place);
    if (sharePct <= 0) continue;
    const profile = profileOf(entry.driverId);
    if (!profile) continue;

    const totalMb = winningsMb * sharePct;
    const driverMb = totalMb * DRIVER_PURSE_SHARE;
    const teamMb = totalMb * TEAM_PURSE_SHARE;
    claimedMb += totalMb;

    const band = purseBandFor(entry.place)?.label ?? '';
    payouts.push({
      place: entry.place,
      driverId: entry.driverId,
      teamId: profile.teamId,
      band,
      sharePct,
      totalMb,
      driverMb,
      teamMb,
    });

    const chain: Modifier[] = [
      { name: 'track purse', value: round2(attendance.trackPurseMb), note: 'SAG/22 — 20% of admissions' },
      { name: 'TV money', value: round2(tv.purseContributionMb), note: 'SAG/21 — 35% of the track\'s share' },
      { name: 'winnings', value: round2(winningsMb), note: 'SAG/23 — purse + TV money' },
      { name: `${band} share`, value: `${round2(sharePct * 100)}%`, note: `${round2(totalMb)} Mb` },
    ];

    push(
      moneyEvent({
        seasonId,
        raceNumber,
        phase: 'post-race',
        account: driverAccount(entry.driverId),
        amountMb: driverMb,
        category: 'winnings',
        description: `${profile.name} finished ${ordinal(entry.place)} — ${round2(totalMb)} Mb of the ${round2(winningsMb)} Mb winnings, of which the driver's 40% is ${round2(driverMb)} Mb.`,
        ruleCitation: `${CITE_PURSE}; ${CITE_PURSE_SPLIT}`,
        modifiers: chain,
      }),
      moneyEvent({
        seasonId,
        raceNumber,
        phase: 'post-race',
        account: teamAccount(profile.teamId),
        amountMb: teamMb,
        category: 'winnings',
        description: `${profile.name} finished ${ordinal(entry.place)} — the team's 60% is ${round2(teamMb)} Mb.`,
        ruleCitation: `${CITE_PURSE}; ${CITE_PURSE_SPLIT}`,
        modifiers: chain,
      }),
    );
  }

  // ---- 4. CONTINGENCY AWARDS (SAG/24) -------------------------------------
  let contingencyMb = 0;
  const award = (driverId: string, amountMb: number, slot: string, why: string): void => {
    const profile = profileOf(driverId);
    if (!profile) return;
    contingencyMb += amountMb;
    const sponsor = CONTINGENCY_SPONSORS[slot];
    push(
      moneyEvent({
        seasonId,
        raceNumber,
        phase: 'post-race',
        account: driverAccount(driverId),
        amountMb,
        category: 'contingency',
        description: sponsor
          ? `${profile.name} collects the ${sponsor.sponsor} award — "${sponsor.tagline}" — ${why}.`
          : `${profile.name} collects ${round2(amountMb)} Mb — ${why}.`,
        ruleCitation: CITE_CONTINGENCY,
      }),
    );
  };

  const poleId = week.grid[0]?.driverId;
  if (poleId) award(poleId, CONTINGENCY_AWARD_MB, 'pole', 'pole position');
  for (const place of CONTINGENCY_FINISH_PLACES) {
    const entry = race.finishingOrder.find((f) => f.place === place);
    if (entry) award(entry.driverId, CONTINGENCY_AWARD_MB, String(place), `${ordinal(place)} place`);
  }
  const leader = standings?.drivers[0];
  if (leader) award(leader.driverId, CONTINGENCY_POINTS_LEADER_MB, 'points-leader', 'leading the point standings');
  if (race.winnerId && previousStyleLastTurnWin(race)) {
    award(race.winnerId, CONTINGENCY_LAST_TURN_WIN_MB, 'last-turn', 'winning it on the last turn');
  }

  const purse: PurseBreakdown = {
    trackPurseMb: attendance.trackPurseMb,
    tvMoneyMb: tv.purseContributionMb,
    winningsMb,
    payouts,
    contingencyMb,
    prizeMb: winningsMb + contingencyMb,
    unclaimedMb: winningsMb - claimedMb,
  };

  // ---- 5. GUARANTEES: sponsorship, automaker support, salary --------------
  const unableIds = new Set(week.unableToRace.map((u) => u.driverId));
  const suspendedIds = new Set(
    week.unableToRace.filter((u) => u.reason === 'suspended').map((u) => u.driverId),
  );

  for (const profile of profiles) {
    const dfs = state.byDriver[profile.driverId] ?? (state.byDriver[profile.driverId] = newDriverFinanceState(profile));

    // -- sponsorship (SAG/14, 16) --
    const blackedOut = dfs.primaryBlackoutRaces > 0;
    const suspended = suspendedIds.has(profile.driverId);

    if (dfs.primary) {
      if (suspended) {
        // SAG/16 — "The Driver's Team receives no sponsorship until the number
        // of race weeks in the suspension period is over."
      } else if (blackedOut) {
        dfs.primaryBlackoutRaces -= 1;
      } else {
        push(
          sponsorPayment(seasonId, raceNumber, profile, dfs.primary, totalRaces, 'sponsorship-primary'),
        );
      }
    }
    if (!suspended) {
      for (const c of dfs.major) {
        push(sponsorPayment(seasonId, raceNumber, profile, c, totalRaces, 'sponsorship-major'));
      }
      for (const c of dfs.associate) {
        push(sponsorPayment(seasonId, raceNumber, profile, c, totalRaces, 'sponsorship-associate'));
      }
    }
    // Defensive: no code path creates a one-race CONTRACT any more (RW52 is
    // paid directly, see step 7), but a contract loaded from an older saved
    // state must not be allowed to keep drawing.
    dfs.associate = dfs.associate.filter((c) => !c.oneRaceOnly);

    // -- automaker support (SAG/25) --
    const supportYearly = AUTOMAKER_SUPPORT_YEARLY[profile.qualities];
    push(
      moneyEvent({
        seasonId,
        raceNumber,
        phase: 'race-week',
        account: teamAccount(profile.teamId),
        amountMb: perRace(supportYearly, totalRaces),
        category: 'automaker-support',
        description: `Automaker support for a ${profile.qualities}-quality driver — ${supportYearly} Mb a year, prorated across ${totalRaces} races.`,
        ruleCitation: CITE_AUTOMAKER,
        modifiers: [{ name: 'qualities', value: profile.qualities, note: 'support falls as qualities rise' }],
      }),
    );

    // -- salary (SAG/31, per D-010) --
    // "Drivers are paid a salary per race even if injured, suspended, etc."
    const salaryYearly = DRIVER_SALARY_YEARLY[profile.qualities];
    const salaryMb = perRace(salaryYearly, totalRaces);
    push(
      ...transfer({
        seasonId,
        raceNumber,
        phase: 'race-week',
        from: teamAccount(profile.teamId),
        to: driverAccount(profile.driverId),
        amountMb: salaryMb,
        category: 'salary',
        description: `${profile.name}'s race salary — ${salaryYearly} Mb a year for ${profile.qualities} qualities, paid whether he raced or not.`,
        ruleCitation: CITE_SALARY,
        modifiers: [{ name: 'qualities', value: profile.qualities }],
      }),
    );
  }

  // ---- 6. COSTS -----------------------------------------------------------
  // Everybody who brought a car to the track pays to be there — including the
  // driver who failed to qualify, who still paid the entry fee, the inspection
  // fee, the travel and the per diem. A driver who could not turn up at all
  // (suspended, injured, dead, no ride) does not.
  const entrantTeamIds = new Set<string>();
  for (const profile of profiles) {
    if (unableIds.has(profile.driverId)) continue;
    entrantTeamIds.add(profile.teamId);
  }

  for (const teamId of [...entrantTeamIds].sort()) {
    push(
      moneyEvent({
        seasonId,
        raceNumber,
        phase: 'race-week',
        account: teamAccount(teamId),
        amountMb: -WEEKLY_RACE_COST_MB,
        category: 'race-cost',
        description: `Weekly race cost — engines, tires, brakes, travel, insurance, per diem, payroll, inspection and entry: ${WEEKLY_RACE_COST_MB} Mb.`,
        ruleCitation: CITE_RACE_COST,
      }),
    );
  }

  // R&D — every team rolls, every race (SAG/30).
  for (const teamId of [...entrantTeamIds].sort()) {
    const roll = dice.sum2d6();
    const costMb = RD_COST_MB[roll.total] * economy.rdScale;
    if (costMb > 0) {
      push(
        moneyEvent({
          seasonId,
          raceNumber,
          phase: 'race-week',
          account: teamAccount(teamId),
          amountMb: -costMb,
          category: 'research-development',
          description: `Research and development on the car of the future: ${costMb} Mb this week.`,
          ruleCitation: economy.rdScale === 1 ? CITE_RD : `${CITE_RD}; ${CITE_RD_SCALE}`,
          dice: [{ kind: '2d6-sum', dice: roll.dice, total: roll.total, label: 'R & D COSTS' }],
          modifiers:
            economy.rdScale === 1
              ? []
              : [
                  { name: 'printed', value: RD_COST_MB[roll.total], note: 'SAG/30 as it reads' },
                  { name: 'league scale', value: economy.rdScale, note: 'D-030' },
                ],
        }),
      );
    }
  }

  // Testing (SAG/30).
  if (testRaces.includes(raceNumber)) {
    const testCostMb = TEST_COST_MB * economy.testScale;
    for (const teamId of [...entrantTeamIds].sort()) {
      push(
        moneyEvent({
          seasonId,
          raceNumber,
          phase: 'race-week',
          account: teamAccount(teamId),
          amountMb: -testCostMb,
          category: 'testing',
          description: `A test session at a league-sanctioned track: ${round2(testCostMb)} Mb.`,
          ruleCitation: CITE_TESTING,
        }),
      );
    }
  }

  // Damage (SAG/29-30). Two sources: a Race Week row that wrecks the car, and
  // the race itself.
  for (const damaged of damagedCars(week, race)) {
    const profile = profileOf(damaged.driverId);
    if (!profile) continue;
    if (damaged.destroyed) {
      push(
        moneyEvent({
          seasonId,
          raceNumber,
          phase: damaged.source === 'race-week' ? 'race-week' : 'race',
          account: teamAccount(profile.teamId),
          amountMb: -CAR_REPLACEMENT_MB,
          category: 'car-replacement',
          description: `${profile.name} destroyed the car — ${damaged.why}. A replacement costs ${CAR_REPLACEMENT_MB} Mb.`,
          ruleCitation: CITE_CAR_REPLACEMENT,
        }),
      );
      continue;
    }
    const roll = dice.sum2d6();
    const costMb = REPAIR_COST_MB[roll.total];
    push(
      moneyEvent({
        seasonId,
        raceNumber,
        phase: damaged.source === 'race-week' ? 'race-week' : 'race',
        account: teamAccount(profile.teamId),
        amountMb: -costMb,
        category: 'repairs',
        description: `${profile.name} — ${damaged.why}. Extraordinary repairs come to ${costMb} Mb on top of the weekly cost.`,
        ruleCitation: CITE_REPAIR_COST,
        dice: [{ kind: '2d6-sum', dice: roll.dice, total: roll.total, label: 'repair cost' }],
      }),
    );
  }

  // Fines (SAG/31-32). The Race Week engine already decided WHO and WHY, off
  // the same chart codes; this books the money and prices the two rows the
  // booklet fines without naming a figure.
  for (const fine of week.fines) {
    const profile = profileOf(fine.driverId);
    if (!profile) continue;
    const amountMb = fine.amount ?? unpricedFineMb;
    const account = fine.payer === 'team' ? teamAccount(profile.teamId) : driverAccount(fine.driverId);
    const priced = fine.amount === null;
    push(
      ...transfer({
        seasonId,
        raceNumber,
        phase: 'race-week',
        from: account,
        to: LEAGUE_ACCOUNT,
        amountMb,
        category: 'fine',
        description: `${profile.name} — ${fine.offense}. Fined ${amountMb} Mb, paid to the league.${priced ? ' (The booklet fines this row without printing an amount; see D-029 §6.)' : ''}`,
        ruleCitation: `${fine.ruleCitation}; ${fine.payer === 'team' ? CITE_TEAM_FINES : CITE_DRIVER_FINES}`,
      }),
    );
  }

  // ---- 7. SPONSORSHIP CONSEQUENCES (SAG/15-19) ----------------------------
  const weekTallies: Record<string, WeekSponsorTally> = {};
  const chainOf = new Map<string, { chart: ChartId; code: string }[]>();
  for (const r of week.tvResults) {
    chainOf.set(r.driverId, r.chain.map((c) => ({ chart: c.chart, code: c.code })));
  }
  for (const [driverId, chain] of chainOf) weekTallies[driverId] = tallyWeek(chain);

  const finishPlace = new Map(race.finishingOrder.map((f) => [f.driverId, f.place]));
  const rankOf = new Map((standings?.drivers ?? []).map((d) => [d.driverId, d.rank]));
  const ordered = [...chainOf.keys()].sort(
    (a, b) => (finishPlace.get(a) ?? 999) - (finishPlace.get(b) ?? 999) || a.localeCompare(b),
  );

  for (const driverId of ordered) {
    const profile = profileOf(driverId);
    const dfs = state.byDriver[driverId];
    if (!profile || !dfs) continue;
    const chain = chainOf.get(driverId) ?? [];

    // Z54 — automatic renewal next season.
    if (chain.some((c) => c.chart === AUTO_RENEW_PRIMARY.chart && c.code === AUTO_RENEW_PRIMARY.code)) {
      dfs.autoRenewPrimary = true;
      sponsorChanges.push({
        driverId,
        kind: 'auto-renew',
        description: `${profile.name}'s primary sponsor has seen enough — the deal renews for next season automatically.`,
        ruleCitation: CITE_AUTO_RENEW,
        dice: [],
        modifiers: [],
      });
    }

    // RW52 — one race's associate money.
    //
    // TWO THINGS THIS IS NOT, both of which it was when first written and both
    // of which the season test caught:
    //
    //   It is NOT a contract. SAG/16 says "Add one week's associate sponsorship
    //   amount to Team" — it adds an AMOUNT, not a sponsor you then hold. Kept
    //   as a contract it occupied one of the three associate slots SAG/15 caps
    //   ("a maximum of… three associate sponsorships at any time"), and a driver
    //   already carrying three ended the week carrying four.
    //
    //   And it pays THIS race, not the next one. Held as a contract it was
    //   signed here, in step 7, after step 5 had already paid out the week's
    //   sponsorships — so it collected on the following weekend and then
    //   expired, which is the opposite of "for this race only".
    //
    // Paid directly, both problems disappear and the money lands on the
    // weekend the booklet says it does.
    if (chain.some((c) => c.chart === ONE_RACE_ASSOCIATE.chart && c.code === ONE_RACE_ASSOCIATE.code)) {
      const yearlyMb = SPONSORSHIP_YEARLY.associate[profile.qualities];
      const amountMb = perRace(yearlyMb, totalRaces);
      push(
        moneyEvent({
          seasonId,
          raceNumber,
          phase: 'race-week',
          account: teamAccount(profile.teamId),
          amountMb,
          category: 'sponsorship-associate',
          description: `A new associate sponsor for this race only — one week of ${yearlyMb} Mb associate money, ${round2(amountMb)} Mb, and extra publicity for ${profile.name}.`,
          ruleCitation: CITE_ONE_RACE_ASSOCIATE,
          modifiers: [{ name: 'priced at qualities', value: profile.qualities }],
        }),
      );
      const special = sponsorNameFor(`${seasonId}|${driverId}|special|${raceNumber}`);
      sponsorChanges.push({
        driverId,
        kind: 'signed-one-race-associate',
        sponsorName: special,
        description: `${special} takes the special sponsor slot on ${profile.name}'s car for this race only — ${round2(amountMb)} Mb this weekend, and gone by the next.`,
        ruleCitation: CITE_ONE_RACE_ASSOCIATE,
        dice: [],
        modifiers: [],
      });
    }
  }

  // New major / associate sponsors (SAG/15-16), in finishing order.
  for (const entry of race.finishingOrder) {
    const profile = profileOf(entry.driverId);
    const dfs = state.byDriver[entry.driverId];
    if (!profile || !dfs) continue;

    const rank = rankOf.get(entry.driverId) ?? 999;
    const tally = weekTallies[entry.driverId];
    const eligibility = signupEligibility(entry.place, rank);
    if (!eligibility) continue;
    if (dfs[eligibility.tier === 'major' ? 'major' : 'associate'].length >= SPONSOR_CAPS[eligibility.tier]) continue;

    const roll = dice.sum2d6();
    const modifiers: Modifier[] = [...eligibility.modifiers];
    for (const e of tally?.effects ?? []) {
      modifiers.push({ name: e.label, value: e.weight, note: `${e.chart} ${e.code}` });
    }
    const bonus = modifiers.reduce((n, m) => n + (typeof m.value === 'number' ? m.value : 0), 0);
    const total = roll.total + bonus;
    const gained = total >= SPONSOR_SIGNUP_SUCCEEDS_AT;

    if (gained) {
      const yearlyMb = SPONSORSHIP_YEARLY[eligibility.tier][profile.qualities];
      const onCar = new Set(
        [dfs.primary, ...dfs.major, ...dfs.associate].map((c) => c?.sponsorName).filter((n): n is string => Boolean(n)),
      );
      const sponsorName = sponsorNameFor(
        `${seasonId}|${entry.driverId}|${eligibility.tier}|${raceNumber}|${dfs.major.length + dfs.associate.length}`,
        onCar,
      );
      dfs[eligibility.tier === 'major' ? 'major' : 'associate'].push({
        tier: eligibility.tier,
        pricedAtQualities: profile.qualities,
        yearlyMb,
        signedRace: raceNumber,
        sponsorName,
        seasonsHeld: 1,
      });
      sponsorChanges.push({
        driverId: entry.driverId,
        kind: eligibility.tier === 'major' ? 'signed-major' : 'signed-associate',
        sponsorName,
        description: `${profile.name} signs ${sponsorName} as a new ${eligibility.tier} sponsor, worth ${yearlyMb} Mb a year, prorated over what is left of the season.`,
        ruleCitation: CITE_SPONSOR_SIGNUP,
        dice: [{ kind: '2d6-sum', dice: roll.dice, total: roll.total, label: 'new sponsor' }],
        modifiers,
      });
    } else {
      sponsorChanges.push({
        driverId: entry.driverId,
        kind: 'declined',
        description: `${profile.name} came up short with the ${eligibility.tier} sponsor — ${roll.total}${bonus >= 0 ? '+' : ''}${bonus} = ${total}, and it takes ${SPONSOR_SIGNUP_SUCCEEDS_AT}.`,
        ruleCitation: CITE_SPONSOR_SIGNUP,
        dice: [{ kind: '2d6-sum', dice: roll.dice, total: roll.total, label: 'new sponsor' }],
        modifiers,
      });
    }
  }

  // Bad mouthing the sponsor (SAG/16-17).
  for (const driverId of ordered) {
    const profile = profileOf(driverId);
    const dfs = state.byDriver[driverId];
    if (!profile || !dfs || !dfs.primary) continue;
    const chain = chainOf.get(driverId) ?? [];
    if (!chain.some((c) => isBadMouth(c.chart, c.code))) continue;

    const roll = dice.sum2d6();
    const modifiers: Modifier[] = [
      { name: 'criticised sponsor', value: -2, note: 'SAG/17 — Y15/Y53' },
    ];
    if (dfs.badMouthWarnings > 0) modifiers.push({ name: 'warned previously this season', value: -1 });
    const place = finishPlace.get(driverId);
    if (place === 1) modifiers.push({ name: 'won the race this week', value: 2 });
    else if (place !== undefined && place <= 5) modifiers.push({ name: 'finished top 5', value: 1 });

    const bonus = modifiers.reduce((n, m) => n + (typeof m.value === 'number' ? m.value : 0), 0);
    const outcome = badMouthOutcome(roll.total + bonus);
    const diceRec: DiceRecord[] = [
      { kind: '2d6-sum', dice: roll.dice, total: roll.total, label: 'Bad Mouthing the Sponsor' },
    ];

    if (outcome === 'lose-a-month') {
      dfs.primaryBlackoutRaces = RACES_PER_MONTH;
      sponsorChanges.push({
        driverId,
        kind: 'primary-blackout',
        description: `${profile.name}'s remarks cost the team a month of primary sponsorship — ${RACES_PER_MONTH} races without a cheque.`,
        ruleCitation: CITE_BAD_MOUTHING,
        dice: diceRec,
        modifiers,
      });
    } else if (outcome === 'warned') {
      dfs.badMouthWarnings += 1;
      sponsorChanges.push({
        driverId,
        kind: 'primary-warned',
        description: `${profile.name} keeps the sponsorship but gets a warning. That is on the record now.`,
        ruleCitation: CITE_BAD_MOUTHING,
        dice: diceRec,
        modifiers,
      });
    } else {
      sponsorChanges.push({
        driverId,
        kind: 'primary-ignored',
        description: `The sponsor ignored it. ${profile.name} got away with one.`,
        ruleCitation: CITE_BAD_MOUTHING,
        dice: diceRec,
        modifiers,
      });
    }
  }

  return {
    raceNumber,
    attendance,
    tv,
    purse,
    events,
    weekTallies,
    tvAppearanceIds: [...week.tvDriverIds],
    sponsorChanges,
    gloomyEconomy,
    state,
  };
}

// ---------------------------------------------------------------------------
// The two big chart computations
// ---------------------------------------------------------------------------

function computeAttendance(args: {
  dice: Dice;
  track: TrackCard;
  raceNumber: number;
  totalRaces: number;
  feuds: boolean;
}): AttendanceBreakdown {
  const { dice, track, raceNumber, totalRaces, feuds } = args;
  const roll = dice.sum2d6();
  const row = ATTENDANCE[roll.total];

  const capacityAssumed = track.capacity == null;
  const capacity = track.capacity ?? DEFAULT_TRACK_CAPACITY;

  const adjustments: Modifier[] = [];
  // SAG/22 — "Other first 25% (rounding up) of all of the year's races".
  const earlyCutoff = Math.ceil(totalRaces * 0.25);
  if (raceNumber === 1) {
    adjustments.push({ name: 'first race of year', value: ATTENDANCE_ADJUSTMENTS.firstRaceOfYear });
  } else if (raceNumber <= earlyCutoff) {
    adjustments.push({
      name: 'early season',
      value: ATTENDANCE_ADJUSTMENTS.earlySeason,
      note: `first 25% of the schedule (races 2-${earlyCutoff})`,
    });
  }
  adjustments.push(
    feuds
      ? { name: 'on-going current feuds', value: ATTENDANCE_ADJUSTMENTS.ongoingFeuds }
      : { name: 'lacking current feuds', value: ATTENDANCE_ADJUSTMENTS.noFeuds },
  );

  const adjustmentPct = adjustments.reduce((n, m) => n + (typeof m.value === 'number' ? m.value : 0), 0);
  // A capacity crowd is a capacity crowd; the grandstand does not grow.
  const finalPct = Math.min(1, Math.max(0, row.pct + adjustmentPct));
  const attendance = Math.round(capacity * finalPct);
  const admissionsMb = attendance * TICKET_PRICE_MB;

  return {
    capacity,
    capacityAssumed,
    roll: roll.total,
    factor: row.factor,
    basePct: row.pct,
    adjustments,
    finalPct,
    attendance,
    ticketPriceMb: TICKET_PRICE_MB,
    admissionsMb,
    trackPurseMb: admissionsMb * TRACK_PURSE_SHARE,
  };
}

function computeTvMoney(args: {
  dice: Dice;
  week: RaceWeekResult;
  race: RaceResult;
  previous: PreviousRaceFacts;
  profiles: Map<string, FinanceDriverProfile>;
  purseShare: number;
}): TvMoneyBreakdown {
  const { dice, week, previous, profiles, purseShare } = args;
  const roll = dice.sum2d6();
  const row = TV_EXTERNAL[roll.total];

  const baseMb = TV_BASE_REVENUE_MB;
  const modifiedBaseMb = baseMb * (1 + row.pct);

  const internal: Modifier[] = [];
  const add = (name: string, pct: number, note?: string): void => {
    internal.push({ name, value: pct, note });
  };

  // The starting grid — positions 1 through 5.
  const poleCard = week.grid[0];
  if (poleCard) {
    const p = profiles.get(poleCard.driverId);
    if (p) {
      // AMBIGUITY (D-029 §10): SAG/21 prints two pole rows —
      //
      //   "Each female or one- or two-quality driver gets the pole (new faces)"  +2%
      //   "An A or B Trophy driver gets the pole (same old faces)"               -2%
      //
      // and never says what to do when one driver satisfies both, which a
      // two-quality gold-trophy card does. Applying both literally cancels them
      // to zero and quietly produces nonsense: the same man is simultaneously a
      // new face and an old one.
      //
      // Ruled MUTUALLY EXCLUSIVE, with the trophy deciding, because the
      // parentheticals are categories rather than modifiers and a trophy IS the
      // record of prior fame — talent is what the quality count measures, and
      // an unknown can be talented. It also makes the pole rows consistent with
      // the top-5 rows immediately below them, which split on trophy alone; read
      // that way, the quality clause exists to widen the "new faces" set beyond
      // C-and-NONE, not to compete with it.
      if (isSameOldFace(p.trophy)) {
        add('same old face on pole', TV_INTERNAL_PCT.sameOldPole, `${p.name}, ${p.trophy} trophy`);
      } else if (p.isFemale || p.qualities <= 2) {
        add('new face on pole', TV_INTERNAL_PCT.newFacePole, `${p.name}, ${p.qualities} qualities`);
      }
    }
  }
  for (let i = 1; i < Math.min(5, week.grid.length); i += 1) {
    const p = profiles.get(week.grid[i].driverId);
    if (!p) continue;
    if (isSameOldFace(p.trophy)) {
      add('same old face in the top 5', TV_INTERNAL_PCT.sameOldTop5, `${p.name} starts ${ordinal(i + 1)}`);
    } else {
      add('new face in the top 5', TV_INTERNAL_PCT.newFaceTop5, `${p.name} starts ${ordinal(i + 1)}`);
    }
  }

  // How many drivers television actually followed this week.
  const tvCount = week.tvDriverIds.length;
  if (tvCount >= 2 && tvCount <= 5) add('thin TV list', TV_INTERNAL_PCT.thinTvList, `${tvCount} drivers`);
  else if (tvCount >= 9) add('busy TV list', TV_INTERNAL_PCT.fatTvList, `${tvCount} drivers`);

  // Last week's race, still selling this week's broadcast.
  if (previous.superRare) add('a SUPER RARE result last week', TV_INTERNAL_PCT.superRareLastWeek);
  if (previous.lastTurnWin) add('last week was won on the final turn', TV_INTERNAL_PCT.lastTurnWin);
  if (previous.bigOne) add('The Big One last week', TV_INTERNAL_PCT.bigOneLastRace);

  // This week's temper.
  const altercation = week.tvResults.some((r) => r.chain.some((c) => isAltercation(c.chart, c.code)));
  if (altercation) add('altercation during Race Week', TV_INTERNAL_PCT.altercationThisWeek);

  const internalPct = internal.reduce((n, m) => n + (typeof m.value === 'number' ? m.value : 0), 0);
  const finalBaseMb = modifiedBaseMb * (1 + internalPct);

  const leagueShareMb = finalBaseMb * TV_LEAGUE_SHARE;
  const trackGrossMb = finalBaseMb - leagueShareMb;

  return {
    baseMb,
    externalRoll: roll.total,
    externalFactor: row.factor,
    externalPct: row.pct,
    modifiedBaseMb,
    internal,
    internalPct,
    finalBaseMb,
    leagueShareMb,
    trackGrossMb,
    trackKeepsMb: trackGrossMb * (1 - purseShare),
    purseContributionMb: trackGrossMb * purseShare,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function sponsorPayment(
  seasonId: string,
  raceNumber: number,
  profile: FinanceDriverProfile,
  contract: SponsorContract,
  totalRaces: number,
  category: 'sponsorship-primary' | 'sponsorship-major' | 'sponsorship-associate',
): MoneyEvent {
  return moneyEvent({
    seasonId,
    raceNumber,
    phase: 'race-week',
    account: teamAccount(profile.teamId),
    amountMb: perRace(contract.yearlyMb, totalRaces),
    category,
    description: `${contract.tier === 'primary' ? 'Primary' : contract.tier === 'major' ? 'Major' : 'Associate'} sponsorship for ${profile.name} — ${contract.yearlyMb} Mb a year at ${contract.pricedAtQualities} qualities, prorated across ${totalRaces} races.`,
    ruleCitation: CITE_SPONSORSHIP,
    modifiers: [{ name: 'priced at qualities', value: contract.pricedAtQualities }],
  });
}

/**
 * SAG/15 — who may roll for what.
 *
 * "If the Driver finished 1st or in the Top 5 (2nd - 5th) in the race, he can
 * roll 2d6 … for a new major sponsor. If the Driver finished Top 10 (6th -
 * 10th) in the race or currently has a Top 10 (2nd -10th) Driver rating (DR),
 * he can roll for a new associate sponsor."
 *
 * RULING (D-029 §7): "Driver rating (DR)" is the championship standing after
 * this race. Sagliano never defines the term, but he uses "Top 10 (2nd-10th)"
 * for it in the same breath as the finishing bands and scores the season-end
 * renewal off "final rank in the racing cup series standings" — the points
 * table is the only ranking the guide maintains.
 *
 * A driver qualifies at the HIGHEST tier he is eligible for; the modifiers for
 * the lower ones still apply, which is what "accumulative" means on p.15.
 */
function signupEligibility(
  place: number,
  rank: number,
): { tier: Exclude<SponsorTier, 'primary'>; modifiers: Modifier[] } | null {
  const modifiers: Modifier[] = [];
  let tier: Exclude<SponsorTier, 'primary'> | null = null;

  if (place === 1) {
    modifiers.push({ name: 'finished 1st', value: 3 });
    tier = 'major';
  } else if (place <= 5) {
    modifiers.push({ name: 'finished top 5', value: 2 });
    tier = 'major';
  } else if (place <= 10) {
    modifiers.push({ name: 'finished top 10', value: 1 });
    tier = 'associate';
  }

  if (rank === 1) {
    modifiers.push({ name: 'top driver rating', value: 1 });
    tier = 'major';
  } else if (rank <= 10) {
    modifiers.push({ name: 'top 10 driver rating', value: 1 });
    tier ??= 'associate';
  }

  return tier ? { tier, modifiers } : null;
}

interface DamagedCar {
  driverId: string;
  destroyed: boolean;
  why: string;
  source: 'race-week' | 'race';
}

/**
 * SAG/29 — "When Race Week or race die rolls indicate a car wreck or damage
 * (Super Rare Results 13 or TV Event X 24, for example), there may be
 * additional expenses repairing the racecar."
 *
 * The Race Week side is a code list of one (X24, "Wrecks car in practice"). The
 * race side is read off the ledger's own moment tags instead of a code list,
 * which is both more complete and impossible to fall out of date: every wreck
 * the engine produces is tagged, by construction.
 *
 * A car that went out of the race is treated as DESTROYED and replaced at 15
 * Mb; a car that was collected and repaired takes the 2d6 repair cost. That is
 * the natural reading of "If a car is totally destroyed, it must be replaced"
 * sitting immediately above the repair chart, and it is flagged as a reading
 * rather than a transcription.
 */
function damagedCars(week: RaceWeekResult, race: RaceResult): DamagedCar[] {
  const out: DamagedCar[] = [];
  const seen = new Set<string>();

  for (const r of week.tvResults) {
    if (r.chain.some((c) => isRaceWeekDamage(c.chart, c.code))) {
      out.push({ driverId: r.driverId, destroyed: true, why: 'wrecked the car in practice', source: 'race-week' });
      seen.add(r.driverId);
    }
  }

  for (const entry of race.retirements) {
    if (seen.has(entry.driverId)) continue;
    if (entry.outReason === 'trouble' || entry.outReason === 'feud' || entry.outReason === 'repair-failed') {
      out.push({
        driverId: entry.driverId,
        destroyed: true,
        why: `out of the race — ${entry.outReason}`,
        source: 'race',
      });
      seen.add(entry.driverId);
    }
  }

  for (const event of race.events) {
    if (!event.momentTag.startsWith('wreck.')) continue;
    for (const id of event.actorIds) {
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({ driverId: id, destroyed: false, why: 'collected in a wreck and repaired', source: 'race' });
    }
  }

  return out;
}

/**
 * SAG/24 — "If in the last lap (last game turn), the driver uses Surge or DUEL
 * to win the race he earns .5 megabuck." Read off the final turn's own events.
 */
function previousStyleLastTurnWin(race: RaceResult): boolean {
  return lastTurnWinMethod(race) !== null;
}

/**
 * HOW the last-turn win was taken, when it was one — SURGE or DUEL — read off
 * the final turn's own events, exactly as the contingency award reads it. The
 * season counts these as wins in their own column (Howard's sheet, "Won by
 * Surge or Duel").
 */
export function lastTurnWinMethod(race: Pick<RaceResult, 'turns' | 'winnerId'>): 'surge' | 'duel' | null {
  const finalTurn = race.turns[race.turns.length - 1];
  if (!finalTurn || !race.winnerId) return null;
  const winner = race.winnerId;
  const decisive = finalTurn.events.find(
    (e) =>
      e.actorIds.includes(winner) &&
      (e.momentTag === 'rare.surge' || e.momentTag.startsWith('duel.')) &&
      e.positionDeltas.some((d) => d.driverId === winner && d.to === 1),
  );
  if (!decisive) return null;
  return decisive.momentTag === 'rare.surge' ? 'surge' : 'duel';
}

/** SAG/24 — the same facts, exported so the season loop can feed next week's TV. */
export function raceFactsForNextWeek(race: RaceResult): PreviousRaceFacts {
  return {
    bigOne: race.events.some((e) => e.momentTag === 'wreck.big-one'),
    superRare: race.events.some((e) => e.modifiers.some((m) => m.name === 'SUPER RARE row')),
    lastTurnWin: previousStyleLastTurnWin(race),
  };
}

/** Every fifth race — 7 tests over 36, never consecutive (SAG/30, D-029 §5). */
export function defaultTestRaces(totalRaces: number): number[] {
  const out: number[] = [];
  for (let r = RACES_PER_TEST; r <= totalRaces; r += RACES_PER_TEST) out.push(r);
  return out;
}

export function newFinanceState(profiles: readonly FinanceDriverProfile[], nameKey?: string): FinanceState {
  const byDriver: Record<string, DriverFinanceState> = {};
  for (const p of profiles) byDriver[p.driverId] = newDriverFinanceState(p, nameKey);
  return { byDriver };
}

/**
 * D-EZ-91 — THE CONTRACTS A SEASON OPENS WITH, CARRIED ACROSS THE WINTER.
 *
 * `carried` is last winter's book after SAG/19's renewals. Every surviving deal
 * comes back on the car with its name and one more season of tenure, RE-PRICED
 * off the driver's new card: SAG/14 prices sponsorship off the qualities a
 * driver has "before the season begins", and the winter just re-rated him. A
 * replacement primary keeps its one-tier discount (`priceOffset`).
 *
 * A driver with no carried book — a rookie, or the first season of a league —
 * opens with the primary every driver has (SAG/14). Warnings, blackouts and
 * Z54's auto-renewal were already wiped by the renewals; they are wiped again
 * here so a carried book can never smuggle one across.
 */
export function openingFinanceState(
  profiles: readonly FinanceDriverProfile[],
  carried: Readonly<Record<string, DriverFinanceState>> | undefined,
  nameKey?: string,
): FinanceState {
  if (!carried) return newFinanceState(profiles, nameKey);
  const byDriver: Record<string, DriverFinanceState> = {};
  for (const p of profiles) {
    const old = carried[p.driverId];
    if (!old) {
      byDriver[p.driverId] = newDriverFinanceState(p, nameKey);
      continue;
    }
    const reprice = (c: SponsorContract): SponsorContract => {
      const q = qualityTier(p.qualities + (c.priceOffset ?? 0));
      const { signedInWinter, ...rest } = c;
      return {
        ...rest,
        pricedAtQualities: q,
        yearlyMb: SPONSORSHIP_YEARLY[c.tier][q],
        signedRace: 0,
        seasonsHeld: (c.seasonsHeld ?? 1) + (signedInWinter ? 0 : 1),
      };
    };
    byDriver[p.driverId] = {
      driverId: p.driverId,
      primary: old.primary
        ? reprice(old.primary)
        : newDriverFinanceState(p, nameKey).primary,
      major: old.major.filter((c) => !c.oneRaceOnly).map(reprice),
      associate: old.associate.filter((c) => !c.oneRaceOnly).map(reprice),
      primaryBlackoutRaces: 0,
      badMouthWarnings: 0,
      autoRenewPrimary: false,
    };
  }
  return { byDriver };
}

function cloneState(state: FinanceState): FinanceState {
  const byDriver: Record<string, DriverFinanceState> = {};
  for (const [id, s] of Object.entries(state.byDriver)) {
    byDriver[id] = {
      ...s,
      primary: s.primary ? { ...s.primary } : null,
      major: s.major.map((c) => ({ ...c })),
      associate: s.associate.map((c) => ({ ...c })),
    };
  }
  return { byDriver };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}
